/**
 * LocaleScope — keeps i18next, <html lang>, and <html dir> in sync with
 * the URL's locale context.
 *
 * Routing approach (i18n 2026-08-27):
 *
 *   <Routes>
 *     {LOCALES.map(l => <Route path={`/${l.code}/*`}> …)}   // prefixed
 *     <Route path="*"> …                                     // legacy
 *   </Routes>
 *
 * Both branches render the SAME route table (AppRoutes in App.tsx) as a
 * descendant <Routes>, which react-router 6 resolves against the portion
 * of the URL left after the parent match — so every existing route works
 * unchanged under /hi/… and unprefixed.
 *
 * Why NOT "/:locale?" (the other candidate pattern): an optional first
 * segment matches ANY single-segment path, so "/cast" would arrive as
 * locale="cast" and every unprefixed route would break. Enumerating the
 * twelve supported codes as explicit patterns keeps legacy URLs
 * byte-compatible and makes unsupported prefixes fall through to the
 * legacy branch naturally.
 *
 * RTL note: this component mirrors CHROME only (document dir + fonts).
 * Chart geometry is class/style-based and is excluded from the logical-
 * property codemod; the wheel never moves.
 */
import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { detectLocale } from "./detect";
import { LOCALE_STORAGE_KEY, localeDir } from "./locales";
import { i18n } from "./index";

export function LocaleScope({
  pathLocale,
  children,
}: {
  /** Locale parsed from the /:locale URL prefix, if this branch is prefixed. */
  pathLocale?: string;
  children: ReactNode;
}) {
  const location = useLocation();

  // Resolution: URL prefix wins (SEO-canonical); otherwise the detect
  // chain (storage → navigator → en). detectLocale re-checks the path,
  // which keeps in-app navigation away from a prefix language-stable via
  // the stored choice.
  const locale = pathLocale ?? detectLocale();

  useEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale);

    const root = document.documentElement;
    if (root.lang !== locale) root.lang = locale;
    // dir mirrors chrome only — never chart geometry.
    const dir = localeDir(locale);
    if (root.getAttribute("dir") !== dir) root.setAttribute("dir", dir);

    // A prefixed hit is intent evidence: persist it so legacy unprefixed
    // hops inside the app keep speaking the same language.
    if (pathLocale) {
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, pathLocale);
      } catch {
        /* storage unavailable — path prefix still governs this page */
      }
    }
  }, [locale, pathLocale, location.pathname]);

  return <>{children}</>;
}

export default LocaleScope;
